import React, { useState } from 'react';
import { 
  Sparkles, 
  Trophy, 
  Building2, 
  Users, 
  ArrowRight, 
  CheckCircle2, 
  Rocket, 
  ShieldCheck,
  Zap,
  Globe,
  BookOpen,
  Wine,
  Waves,
  Users2,
  GraduationCap,
  Briefcase,
  Heart,
  Megaphone,
  Palette,
  Scale,
  Scroll,
  Wallet,
  Database,
  ChevronLeft,
  ChevronRight,
  Coffee,
  Home
} from 'lucide-react';

import { motion } from 'motion/react';
import { Button, Card, Badge } from '../components/ui';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { cn } from '../lib/utils';
import { SEO } from '../components/SEO';

export const UseCasesPage = () => {
  const brandName = 'OrgSets';

  const categories = [
    { id: 'hobbyist', label: 'Hobbyist Groups', icon: Sparkles },
    { id: 'sports', label: 'Sports Leagues', icon: Trophy },
    { id: 'swim-teams', label: 'Swim Teams', icon: Waves },
    { id: 'business', label: 'Business Networks', icon: Building2 },
    { id: 'alumni', label: 'Alumni Networks', icon: GraduationCap },
    { id: 'professional', label: 'Professional Bodies', icon: Briefcase },
    { id: 'nonprofit', label: 'Non-Profits', icon: Heart },
    { id: 'advocacy', label: 'Advocacy Groups', icon: Megaphone },
    { id: 'social', label: 'Social Clubs', icon: Users2 },
    { id: 'creative', label: 'Studio Networks', icon: Palette },
    { id: 'heritage', label: 'Heritage Societies', icon: Scroll },
    { id: 'senior', label: 'Senior Communities', icon: Coffee },
    { id: 'hoa', label: 'Homeowner Associations', icon: Home },
  ];

  const transformations = [
    {
      id: 'hobbyist',
      icon: Sparkles,
      title: "From a Hobby to a Community",
      from: "Messy group chats and lost Facebook posts.",
      to: "A clear, private home where everything is easy to find.",
      target: "Book Clubs, Gaming Groups, Collectors",
      benefit: "Whether you're starting a small club or a growing hobby group, we help you keep everyone connected without the stress of managing multiple apps.",
      details: [
        "Private Member List: A simple, secure list so you can easily reach out to friends without digging through old chats.",
        "Shared Resource Library: A central home for all your group's treasures—lists, guides, and plans.",
        "Friendly Notifications: Important updates stay at the top so everyone knows what's happening."
      ],
      toolLink: '/tools/sign-up-sheet-generator',
      toolLabel: 'Try the Free Sign-Up Sheet Generator'
    },
    {
      id: 'sports',
      icon: Trophy,
      title: "From local team to Regional League",
      from: "Lost clipboards and physical sign-up sheets.",
      to: "A streamlined home to manage your whole league with clarity.",
      target: "Youth Sports, Fitness Groups, Esports",
      benefit: "Focus on the game, not the paperwork. We help you manage your teams and members with ease.",
      details: [
        "Simple Registration: Easy sign-ups for players and parents.",
        "Safe & Secure: Keep all your important documents and waivers in one private vault.",
        "Clear Goals: Track your team's progress and fundraising together."
      ],
      modeledValue: {
        badge: "100% On-Time Dues",
        scale: "Modeled for a 25-Team Youth/Adult League (~600 Families)",
        points: [
          "100% on-time registration dues collected online with zero manual Venmo chasing",
          "1-click emergency weather & cancellation SMS delivered in seconds to all parents",
          "Digital liability waiver vault with 100% pre-season compliance signed at checkout"
        ]
      },
      toolLink: '/tools/dues-split-calculator',
      toolLabel: 'Try the Dues Split Calculator'
    },
    {
      id: 'swim-teams',
      icon: Waves,
      title: "From Scattered Swimmers to Seeded Meets",
      from: "Messy entry sheets, manual lane seeding, and parent volunteer chaos on paper.",
      to: "Automated spearhead lane seeding, instant digital heat sheets, and real-time coordinator alerts.",
      target: "Swim Clubs, Summer Leagues, Swim Coordinators",
      benefit: "Coordinate swimmer entries, automate seed times and heat sheet generation, and assign volunteer roles seamlessly for every swim meet.",
      details: [
        "Automated Heat Seeding: Generate perfect heat sheets with standard spearhead lane ordering in seconds.",
        "Parent & Volunteer Portals: Let parents track entries, sign waivers, and sign up for timer/marshal shifts.",
        "Real-Time Broadcasts: Send heat starts and emergency weather alerts instantly to all parents' phones."
      ],
      toolLink: '/tools/swim-heat-sheet-generator',
      toolLabel: 'Try the Swim Heat Sheet Generator'
    },
    {
      id: 'business',
      icon: Building2,
      title: "From a Single Store to a Global Franchise",
      from: "Different ways of working at every location and messy group chats.",
      to: "One beautiful home for your whole brand, with local pride.",
      target: "Retail, Franchises, Professional Services",
      benefit: "Keep everyone connected to the big picture while giving local owners the tools they need to shine in their community.",
      details: [
        "Brand Harmony: A beautiful, consistent look for every single location.",
        "Instant Updates: Reach every member of your team across the world in seconds.",
        "Role-Based Access: Simple tools to manage who sees what at every level."
      ],
      toolLink: '/tools/membership-certificate',
      toolLabel: 'Try the Membership Certificate Generator'
    },
    {
      id: 'alumni',
      icon: GraduationCap,
      title: "Graduates to Endowment Network",
      from: "Mailing lists and LinkedIn groups that nobody checks.",
      to: "A verified portal for mentoring, giving, and career tracking.",
      target: "Alumni Associations, Bootcamp Grads, Company Retirees",
      benefit: "Turn nostalgic connections into a powerful, self-sustaining endowment engine.",
      details: [
        "Mentorship Matching: Connect senior alumni with newcomers.",
        "Endowment Ledger: Transparent tracking of scholarship funds.",
        "Global Directory: Search members by industry, year, or location."
      ],
      modeledValue: {
        badge: "Endowment Acceleration",
        scale: "Modeled for a 1,500-Member Alumni Network",
        points: [
          "Direct Stripe endowment and scholarship contribution routing with instant receipts",
          "Self-updating member directory replacing outdated email spreadsheets",
          "Verified career and mentorship connection channels"
        ]
      },
      toolLink: '/tools/alumni-mentorship-match',
      toolLabel: 'Try the Alumni Mentorship Matcher'
    },
    {
      id: 'professional',
      icon: Briefcase,
      title: "Independent Pros to Accredited Body",
      from: "Manual certification tracking and fragmented email threads.",
      to: "Automated credentialing with a transparent ethics ledger.",
      target: "Trade Unions, Medical Societies, Professional Guilds",
      benefit: "Establish authority in your industry with standardized rules for every member.",
      details: [
        "Credential Hosting: Store and verify professional certifications.",
        "Ethics Vault: Securely manage grievances and disciplinary records.",
        "Direct Upstream Comms: Policy updates travel straight to policy-makers."
      ],
      toolLink: '/tools/certification-pricing-calculator',
      toolLabel: 'Try the Certification Pricing Calculator'
    },
    {
      id: 'nonprofit',
      icon: Heart,
      title: "From Local Volunteers to National Impact",
      from: "Fundraising efforts that get lost between layers.",
      to: "A clear view of your mission's impact and easy coordination everywhere.",
      target: "Charities, NGOs, Advocacy Groups",
      benefit: "Grow your mission from one city to every state without losing the personal touch that makes your volunteers special.",
      details: [
        "Clear Impact Tracking: See exactly how every contribution helps your mission grow.",
        "Shared Goals: Work together on big targets, from local events to national campaigns.",
        "Volunteer Connection: Reach your supporters in seconds when they're needed most."
      ],
      modeledValue: {
        badge: "Mission Transparency",
        scale: "Modeled for a Multi-Chapter Volunteer Organization",
        points: [
          "Centralized campaign ledgers show national vs local contribution allocations in real time",
          "Rapid volunteer SMS alerts for emergency initiatives and event shifts",
          "Permanent storage for 501(c)(3) filings, meeting minutes, and audit trails in the vault"
        ]
      },
      toolLink: '/tools/volunteer-value-calculator',
      toolLabel: 'Try the Volunteer Value Calculator'
    },
    {
      id: 'advocacy',
      icon: Megaphone,
      title: "Grassroots Action to Policy Powerhouse",
      from: "Viral social posts that don't translate into local policy changes.",
      to: "Strict governance tiers for handling sensitive strategic blueprints.",
      target: "Political Movements, Union Organizing, Environmental Groups",
      benefit: "Turn digital energy into a disciplined organization that wins.",
      details: [
        "Voter Vetting: Ensure your voting body is verified and eligible.",
        "Strategy Blueprints: Securely store campaign plans in the Vault.",
        "Mass Mobilization: Broadcast critical instructions to field organizers."
      ],
      toolLink: '/tools/canvassing-route-optimizer',
      toolLabel: 'Try the Canvassing Route Optimizer'
    },
    {
      id: 'social',
      icon: Users2,
      title: "Social Club to Structured Order",
      from: "Dues paid in cash at the bar and paper meeting minutes.",
      to: "Digital dues split across national/local ledgers with voting.",
      target: "Fraternities, Service Clubs, Private Societies",
      benefit: "Preserve legacy traditions while modernizing the management of growth.",
      details: [
        "Dues Splitting: Automatic distribution to local and national pots.",
        "Digital Minutes: Secure, searchable archive of all board meetings.",
        "Election Guard: Anonymous, cryptographic leadership elections."
      ],
      modeledValue: {
        badge: "$3,000–$5,000/yr Saved",
        scale: "Modeled for a 12-Chapter Network (~1,200 Members)",
        points: [
          "Eliminates per-contact penalty pricing and 2–4% commission cuts from legacy platforms",
          "Automated Stripe split-engine routes HQ dues share and chapter bank accounts directly",
          "Role-based document vault guarantees bylaws and charters survive officer transitions"
        ]
      },
      toolLink: '/tools/dues-split-calculator',
      toolLabel: 'Try the Dues Split Calculator'
    },
    {
      id: 'creative',
      icon: Palette,
      title: "Freelance Circle to Global Studio",
      from: "Siloed portfolios and disjointed project management.",
      to: "Unified studio branding with hierarchical node accountability.",
      target: "Design Collectives, Production Houses, Music Labels",
      benefit: "Collaborate at scale while protecting each studio's creative autonomy.",
      details: [
        "Studio Referrals: Incentivize growth through a referral engine.",
        "Asset Vault: Share high-value creative assets across permissions.",
        "Project Ledgers: Track revenue splits on collaborative projects."
      ],
      toolLink: '/tools/sponsorship-package-pricer',
      toolLabel: 'Try the Sponsorship Package Pricer'
    },
    {
      id: 'heritage',
      icon: Scroll,
      title: "Lineage Society to National Archive",
      from: "Fragmented genealogy proofs spread across regional curators and paper folders.",
      to: "3-Tiered ledger system (National-State-Local) with an encrypted Lineage Vault.",
      target: "Heritage Societies, Lineage Groups, Historical Chapters",
      benefit: "Protect your historical integrity while automating the complex dues split across all levels of your organization.",
      details: [
        "3-Tier Ledger: Automatically distribute National, State, and Local dues in one click.",
        "Lineage Vault: Hosted on SOC 2 & ISO 27001 certified AWS infrastructure with 256-bit encryption for genealogical proofs and historical charters.",
        "Global Policies: Send bylaws and ceremonial protocols instantly from National HQ to every local chapter."
      ],
      toolLink: '/tools/bylaws-templates-builder',
      toolLabel: 'Try the Bylaws Templates Builder'
    },
    {
      id: 'senior',
      icon: Coffee,
      title: "From a Noticeboard to a Vibrant Social Scene",
      from: "Flyers in the hallway, forgotten game schedules, and missed connections.",
      to: "A lively, accessible community organization for activity scheduling and finding local friends.",
      target: "Senior Living, Retirement Communities, Active Adult Centers",
      benefit: "Keep residents engaged, active, and socially connected with accessible scheduling and interest-matching.",
      details: [
        "Activity Calendar: Easy online scheduling and sign-ups for Bingo, water aerobics, and weekly Bridge tournaments.",
        "Social Directory: Find neighbors who share your interests for a game of Canasta, shuffleboard, or a morning walk.",
        "Clear Communication: High-contrast, easy-to-read daily announcements and newsletters."
      ],
      toolLink: '/tools/bingo-caller-board',
      toolLabel: 'Try the Bingo Caller Board'
    },
    {
      id: 'hoa',
      icon: Home,
      title: "From HOA Burnout to Harmonious Living",
      from: "Board meetings dominated by disputes, paper checks lost in the mail, and undocumented covenants.",
      to: "Automated assessment ledgers, a public digital vault for bylaws, and transparent, tamper-evident audit trails.",
      target: "Homeowners Associations, Condo Boards, Tenant Unions",
      benefit: "Eliminate the administrative friction of managing your community. Automate dues collection, secure covenants, and build resident trust with absolute clarity.",
      details: [
        "Automated Assessment Ledgers: Residents pay online via secure recurring bank transfers or card payments.",
        "Centralized Vault Archive: Instant access for residents to bylaws, covenants, and approved meeting minutes.",
        "Tamper-Evident Audits: Permanent logs of every transaction protecting the board from liability and disputes."
      ],
      modeledValue: {
        badge: "0 Lost Records",
        scale: "Modeled for a 350-Home Community Association",
        points: [
          "Automated bank transfer (ACH) and card payments eliminate paper checks lost in the mail",
          "Encrypted digital vault ensures incoming board treasurers inherit audit-ready books",
          "Tamper-evident logs of every transaction protect board officers from disputes"
        ]
      },
      toolLink: '/tools/hoa-budget-tracker',
      toolLabel: 'Try the HOA Budget Tracker'
    }
  ];

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to move your organization to OrgSets",
    "description": "Migrate your members and start using OrgSets in minutes.",
    "step": [
      { "@type": "HowToStep", "text": "Create your organization and set its name." },
      { "@type": "HowToStep", "text": "Invite your members or import your existing directory." },
      { "@type": "HowToStep", "text": "Set up your communication channels and start operating." }
    ]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What types of organizations can use OrgSets?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "OrgSets works for HOAs, sports leagues, swim teams, nonprofits, fraternities, sororities, alumni associations, churches, professional guilds, advocacy groups, senior communities, heritage societies, and any group with a hierarchical structure."
        }
      },
      {
        "@type": "Question",
        "name": "Can OrgSets manage HOA dues and assessments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. OrgSets provides automated assessment ledgers with online payment processing, a centralized vault for bylaws and covenants, and tamper-evident audit trails for full financial transparency."
        }
      },
      {
        "@type": "Question",
        "name": "How does OrgSets help sports leagues and swim teams?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "OrgSets offers simple registration, automated heat sheet seeding for swim meets, parent and volunteer portals, real-time broadcast alerts, and a secure vault for waivers and documents."
        }
      },
      {
        "@type": "Question",
        "name": "Is OrgSets free to start?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. OrgSets offers a free tier for small groups, plus free standalone tools like the HOA Budget Tracker, Dues Split Calculator, Swim Heat Sheet Generator, and Meeting Agenda Builder."
        }
      },
      {
        "@type": "Question",
        "name": "Can OrgSets replace spreadsheets and group chats for my organization?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely. OrgSets centralizes member management, dues collection, document storage, event scheduling, and communications into a single platform — eliminating scattered spreadsheets, WhatsApp groups, and email chains."
        }
      }
    ]
  };

  const pageSchemas = [howToSchema, faqSchema];

  return (
    <div className="min-h-screen font-sans bg-white text-navy-900 dark:text-white dark:bg-[#050505]">
      <SEO 
        title="Organization Management for HOAs, Leagues, Nonprofits & Clubs" 
        description="See how OrgSets replaces spreadsheets and group chats for HOAs, sports leagues, swim teams, nonprofits, fraternities, alumni networks, and professional bodies. Free tools included."
        keywords="organization management software, hoa management software, sports league management, swim team management, nonprofit management platform, fraternity management tool, alumni association software, community management"
        schema={pageSchemas}
      />
      <PublicHeader />

      <main className="pb-24">
        {/* Hero */}
        <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black mb-24">
          <div className="p-8 pt-28 md:p-16 md:pt-32 lg:p-12 lg:pt-32 xl:p-24 xl:pt-40 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
            <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
              Use Cases & <br/> Architectures
            </h1>
            <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
              Start with a small group of friends and watch it flourish. {brandName} provides the tools to manage leagues, chapters, HOAs, and nonprofits without the overhead.
            </p>
          </div>
          <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-secondary rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-500 rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
            
            <div className="relative z-10 space-y-4">
              <div className="border-[4px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white p-4 font-black uppercase tracking-widest text-2xl shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] -rotate-2">SPORTS LEAGUES</div>
              <div className="border-[4px] border-black dark:border-white bg-brand-secondary text-black p-4 font-black uppercase tracking-widest text-2xl shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] rotate-1">HOAs & CONDOS</div>
              <div className="border-[4px] border-black dark:border-white bg-pink-500 text-white p-4 font-black uppercase tracking-widest text-2xl shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] -rotate-1">NON-PROFITS</div>
            </div>
          </div>
        </section>

        <div className="px-4">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12">
          {/* Side Nav */}
          <aside className="lg:w-64 shrink-0">
            <div className="sticky top-32 space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-40 mb-4 px-4 font-mono">Jump to Category</p>
              {transformations.map(t => (
                <a 
                  key={t.id} 
                  href={`#${t.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-brand-primary/5 transition-all group"
                >
                  <t.icon size={18} className="text-brand-primary opacity-60 group-hover:opacity-100" />
                  <span className="text-sm font-bold opacity-60 group-hover:opacity-100">{t.title.split(' to ')[0]}</span>
                </a>
              ))}
              <div className="pt-8 px-4">
                <div className="p-4 bg-black text-white dark:bg-white dark:text-black border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]">
                  <p className="text-xs font-black uppercase tracking-widest mb-2">Ready to switch?</p>
                  <p className="text-[10px] opacity-80 mb-4 leading-relaxed font-bold">Migrate your members from Facebook or WhatsApp in 5 minutes.</p>
                  <Button size="sm" className="w-full text-xs h-8 border-[2px] border-transparent bg-white text-black dark:bg-black dark:text-white rounded-none hover:bg-[#FFD600] font-bold uppercase" onClick={() => window.location.href = '/sign-up'}>Get Started</Button>
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 space-y-16 md:space-y-24">
            {transformations.map((item, i) => (
              <section key={item.id} id={item.id} className="scroll-mt-32">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                  <div>
                    <Badge variant="blue" className="mb-4">{item.target.split(',')[0]}</Badge>
                    <h2 className="text-3xl md:text-5xl font-black font-display tracking-tight mb-6">
                      {item.title}
                    </h2>
                    <p className="text-lg opacity-60 leading-relaxed mb-8">
                      {item.benefit}
                    </p>

                    <div className="space-y-6">
                      {item.details.map((detail, idx) => (
                        <div key={idx} className="flex gap-4">
                          <div className="shrink-0 w-6 h-6 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center">
                            <CheckCircle2 size={14} />
                          </div>
                          <p className="text-sm font-medium">{detail}</p>
                        </div>
                      ))}
                    </div>

                    {(item as any).toolLink && (
                      <Link
                        to={(item as any).toolLink}
                        className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-primary hover:underline group"
                      >
                        {(item as any).toolLabel}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    )}
                    <Link
                      to={`/use-cases/${item.id === 'sports' ? 'sports-leagues' : item.id === 'social' ? 'social-clubs' : item.id === 'swim-teams' ? 'swim-teams' : item.id}`}
                      className="mt-3 inline-flex items-center gap-2 text-sm font-black uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity group"
                    >
                      Learn More
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>

                  <div className="p-8 lg:p-12 h-full bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
                    <div className="mb-8 p-4 border-[2px] border-black dark:border-white inline-block bg-[#FFD600] text-black shadow-[4px_4px_0px_#000]">
                      <item.icon size={40} />
                    </div>
                    <div className="space-y-8">
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#FC3D21] block mb-2 font-mono">The Chaos (Current)</span>
                        <div className="p-4 border-[2px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A]">
                          <p className="text-sm font-bold leading-relaxed">{item.from}</p>
                        </div>
                      </div>
                      <div className="flex justify-center">
                        <ArrowRight className="text-black dark:text-white" size={24} />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-green-600 dark:text-green-400 block mb-2 font-mono">The Order (With {brandName})</span>
                        <div className="p-4 border-[2px] border-black dark:border-white bg-[#0044FF] text-white">
                          <p className="text-sm font-bold leading-relaxed">{item.to}</p>
                        </div>
                      </div>

                      {item.modeledValue && (
                        <div className="p-4 border-[2px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A]">
                          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                            <span className="text-[10px] font-mono font-black uppercase tracking-wider text-[#0044FF] dark:text-[#FFD600]">
                              Potential Value & Modeled Impact
                            </span>
                            <span className="text-[10px] font-mono font-black px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 border border-emerald-500/40">
                              {item.modeledValue.badge}
                            </span>
                          </div>
                          <div className="text-xs font-mono text-gray-500 dark:text-gray-400 mb-2 font-bold">
                            {item.modeledValue.scale}
                          </div>
                          <ul className="space-y-1.5 text-xs font-bold text-gray-700 dark:text-gray-300">
                            {item.modeledValue.points.map((pt, pIdx) => (
                              <li key={pIdx} className="flex items-start gap-2">
                                <CheckCircle2 size={13} className="text-emerald-500 shrink-0 mt-0.5" />
                                <span>{pt}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>



        {/* Related Free Tools */}
        <section className="py-16 md:py-24 px-4 bg-[#F5F5F0] dark:bg-[#0A0A0A] border-y-[8px] border-black dark:border-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-black font-display tracking-tight mb-4 uppercase">Explore Our Free Tools</h2>
            <p className="opacity-80 mb-8 font-bold">Not ready for a full workspace? Try our standalone utilities for specific organizational needs.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/tools/hoa-budget-tracker" className="bg-white text-black dark:bg-black dark:text-white border-[2px] border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center">HOA Budget Tracker</Link>
              <Link to="/tools/dues-split-calculator" className="bg-white text-black dark:bg-black dark:text-white border-[2px] border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center">Dues Split Calculator</Link>
              <Link to="/tools/bylaws-templates-builder" className="bg-white text-black dark:bg-black dark:text-white border-[2px] border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center">Bylaws Templates Builder</Link>
              <Link to="/tools/swim-heat-sheet-generator" className="bg-white text-black dark:bg-black dark:text-white border-[2px] border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff] font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0px_#000] transition-all inline-flex items-center">Swim Heat Sheet Generator</Link>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-16 md:py-24 px-4 bg-white dark:bg-black border-b-[8px] border-black dark:border-white">
          <div className="max-w-3xl mx-auto text-center border-[4px] border-black dark:border-white p-8 md:p-12 shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] bg-[#F5F5F0] dark:bg-[#0A0A0A]">
            <h2 className="text-4xl md:text-6xl font-black font-display tracking-tight mb-8 uppercase">
              Stop surviving <br />Start thriving.
            </h2>
            <p className="text-xl opacity-80 mb-12 font-bold">
              Whether you're a wine tasting group of five or a national association of 50,000—we provide the structure for your vision.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/sign-up" className="h-16 px-12 text-xl font-bold rounded-none border-[4px] border-black dark:border-white bg-black text-white dark:bg-white dark:text-black w-full sm:w-auto uppercase hover:bg-[#FFD600] hover:text-black transition-colors inline-flex items-center justify-center">
                Get Started for Free
              </Link>
              <Link to="/" className="h-16 px-12 font-black uppercase tracking-widest border-[4px] border-transparent hover:border-black dark:hover:border-white rounded-none inline-flex items-center justify-center">
                Back to Overview
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};
